import { subscribeEvents } from '../events';
import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, serverInUseDetails, type ReadinessCheck, type ServerCapability } from '../api';
import { useQuery, useResource } from '../hooks';
import { queries } from '../queries';
import { humanize, statusFor } from '../labels';
import { ConfirmDialog, ErrorNotice, ServerInUseNotice, SkeletonPage, StatusBadge } from '../components/ui';
import { actionLabel } from '../components/operation/actionLabels';
import { serverRemovalConsequences } from './ServersPage';
import { ActionMenu, Callout, Icon, Page, PageHeader, Section, Subsection, SummaryTile, SummaryTiles } from '../components/primitives';
import { relativeTime } from '../components/project/projectHealth';
import { agentStateLabel } from '../components/project/operationalHealth';
import { HostedEnvironments } from '../components/portfolio/HostedEnvironments';
import { ServerEdgeSection } from '../components/server/ServerEdgeSection';
import { ServerCloudSection } from '../components/server/ServerCloudSection';
import { ServerTerminalSection } from '../components/server/ServerTerminalSection';
import type { HealthLevel, Tone } from '../labels';
import { isOutOfDate, kernelRelease, sameInstant } from '../components/server/freshness';

const toneLevel: Record<Tone, HealthLevel> = { success: 'ok', warning: 'warning', danger: 'critical', info: 'unknown', neutral: 'unknown', progress: 'unknown' };
import { LevelBadge, QuietEmpty } from '../components/project/ui';
import { FilterChips } from '../components/primitives';
import { HelpTip } from '../components/primitives/HelpTip';

/** One compact row per check, with its category and the raw data on demand. */
function CheckList({
  checks,
  expanded,
  onToggle,
  categoryTitles,
}: {
  checks: ReadinessCheck[];
  expanded: Record<string, boolean>;
  onToggle: (id: string) => void;
  categoryTitles: Record<string, string>;
}) {
  return (
    <ul className="check-list">
      {checks.map((check) => {
        const hasData = check.data && Object.keys(check.data).length > 0;
        const open = expanded[check.id];
        return (
          <li key={check.id} className="check-list__item">
            <div className="check-list__text">
              <strong>{check.title}</strong>
              <span className="type-description">{check.message}</span>
              <span className="type-meta">{categoryTitles[check.category || 'other'] || humanize(check.category || 'other')}</span>
            </div>
            <span className="check-list__badges">
              <StatusBadge kind="serverCheckRow" value={check.status} />
              {hasData ? (
                <button type="button" className="button button--ghost button--small" aria-expanded={Boolean(open)} onClick={() => onToggle(check.id)}>
                  {open ? actionLabel.hideDetails : actionLabel.viewDetails}
                </button>
              ) : null}
            </span>
            {open && hasData ? <pre className="check-list__data">{JSON.stringify(check.data, null, 2)}</pre> : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * One capability: name and verdict on one line, the reason under them at
 * full width, and for anything short of "supported" whether it blocks and
 * what to do. Badges never share a column with the text, so narrow
 * columns cannot crush it into a word per line.
 */
function CapabilityRow({ cap, checkedAt }: { cap: ServerCapability; checkedAt?: string }) {
  const explained = cap.status !== 'supported' && cap.status !== 'available' && cap.status !== 'recommended';
  // Rows read from the server check share its time, shown once on the
  // section (with "out of date" once); a row with a time of its own (the
  // agent connection) shows it, and its own staleness.
  const ownTime = cap.observed_at && !sameInstant(cap.observed_at, checkedAt) ? cap.observed_at : undefined;
  const outOfDate = ownTime ? isOutOfDate(ownTime) : false;
  // Out-of-date evidence does not support a green verdict: the word stays,
  // the tone is the unknown one. A problem stays as loud as it was.
  const stale = (outOfDate || (!ownTime && isOutOfDate(checkedAt))) && statusFor('capability', cap.status).tone === 'success';
  return (
    <li className="capability-row" data-testid={`capability-${cap.id}`}>
      <div className="capability-row__head">
        <strong>{cap.name}</strong>
        <span className="capability-row__badges">
          {ownTime ? (
            <time className="type-meta" dateTime={ownTime} title={new Date(ownTime).toLocaleString()}>
              {relativeTime(ownTime)}
            </time>
          ) : null}
          {outOfDate ? <StatusBadge kind="capability" value="unknown" label="Out of date" /> : null}
          {cap.ownership && cap.ownership !== 'unknown' ? <StatusBadge kind="ownership" value={cap.ownership} /> : null}
          {stale ? (
            <StatusBadge kind="capability" value="unknown" label={statusFor('capability', cap.status).label} title="Out of date" />
          ) : (
            <StatusBadge kind="capability" value={cap.status} />
          )}
        </span>
      </div>
      <p className="capability-row__reason">{cap.reason}</p>
      {explained && cap.remedy && cap.status === 'unknown' ? (
        // "Not detected" is not a warning: say what it means, without the
        // emphasis a warning gets.
        <p className="capability-row__remedy">{cap.remedy}</p>
      ) : explained && cap.remedy ? (
        <p className="capability-row__remedy">
          <span className={cap.impact === 'blocking' ? 'capability-row__impact capability-row__impact--blocking' : 'capability-row__impact'}>
            {cap.impact === 'blocking' ? 'Blocks this until fixed.' : 'Does not block deployments.'}
          </span>{' '}
          {cap.remedy}
        </p>
      ) : null}
    </li>
  );
}

/** The server check's overall result, in the words of the servers list. */
const auditOverall: Record<string, string> = { pass: 'ready', warning: 'warning', fail: 'failed' };

const CAPABILITY_CATEGORY_TITLES: Record<string, string> = {
  deployment: 'Deployment',
  infrastructure: 'Web server and reverse proxy',
  database: 'Databases',
  backup: 'Backups and storage',
  monitoring: 'Monitoring',
  storage_compute: 'CPU, memory and disk',
  agent: 'Agent',
  runtime: 'Programs installed on the host',
};

const CATEGORY_TITLES: Record<string, string> = {
  os: 'Operating system',
  cpu: 'Processor and load',
  memory: 'Memory and swap',
  disk: 'Disk',
  network: 'Network and ports',
  services: 'Software already on the server',
  docker: 'Docker',
  permissions: 'Agent permissions',
  security: 'Security hardening',
};

/**
 * How long a server check may take before the page says it has not finished.
 * The check is read-only and normally returns in seconds; this is a limit on
 * waiting, not a claim about what went wrong (CO-184).
 */
export const SERVER_CHECK_WAIT_MS = 3 * 60 * 1000;

export function ServerDetailPage() {
  const { serverId } = useParams<{ serverId: string }>();
  const navigate = useNavigate();
  const [reloadToken, setReloadToken] = useState(0);

  const [isRunningAudit, setIsRunningAudit] = useState(false);
  // The check was asked for and no result arrived in time.
  const [auditStalledAt, setAuditStalledAt] = useState<number | null>(null);
  const [auditError, setAuditError] = useState<Error | null>(null);

  const [activeFilter, setActiveFilter] = useState<'all' | 'pass' | 'warning' | 'fail'>('all');
  const [expandedChecks, setExpandedChecks] = useState<Record<string, boolean>>({});
  const [showAllCapabilities, setShowAllCapabilities] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [reinstallToken, setReinstallToken] = useState<string | null>(null);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);

  const serverResource = useResource(
    () => (serverId ? api.getServer(serverId) : Promise.reject(new Error('Missing serverId'))),
    [serverId, reloadToken], undefined, { key: `server:${serverId}` },
  );

  const workspace = useResource(api.workspace, [], undefined, { key: 'workspace' });
  const identity = workspace.data?.identity;
  const isOwner = Boolean(identity?.role?.is_owner_role || identity?.kind === 'dev_workspace');
  const canOpenTerminal = isOwner || Boolean(identity?.capabilities?.includes('manage_terminal'));

  const server = serverResource.data?.server;
  const latestAudit = serverResource.data?.latest_audit;
  // The agent verdict and what this server hosts, from the one derivation
  // the Dashboard and the projects use (CO-96).
  const health = useQuery(queries.organizationHealth(), [reloadToken]);
  const hosting = health.data?.servers.find((entry) => entry.server_id === serverId);
  // A check older than a week describes the server as it was (UX-16).
  const checkIsOld = isOutOfDate(latestAudit?.created_at);

  // Live updates from the shared events connection
  useEffect(() => {
    if (!serverId) return;

    // One audit publishes two events (readiness.completed and
    // server.capabilities.updated); coalesce them into one reload.
    let reloadTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleReload = () => {
      if (reloadTimer) clearTimeout(reloadTimer);
      reloadTimer = setTimeout(() => setReloadToken((t) => t + 1), 250);
    };

    const unsubscribe = subscribeEvents((data) => {
      if (data.type === 'events.reconnected') scheduleReload();
      if (data.serverId !== serverId) return;
      scheduleReload();
      if (data.type === 'readiness.completed') {
        setIsRunningAudit(false);
        setAuditStalledAt(null);
      }
    });

    return () => {
      unsubscribe();
      if (reloadTimer) clearTimeout(reloadTimer);
    };
  }, [serverId]);

  // A check whose result never arrives must not spin for ever: after a wait
  // the page says so and offers Retry. Nothing is assumed about the cause.
  useEffect(() => {
    if (!isRunningAudit) return;
    const timer = setTimeout(() => {
      setIsRunningAudit(false);
      setAuditStalledAt(Date.now());
    }, SERVER_CHECK_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isRunningAudit]);

  const handleRunAudit = async () => {
    if (!serverId || isRunningAudit) return;
    setIsRunningAudit(true);
    setAuditStalledAt(null);
    setAuditError(null);
    try {
      await api.triggerServerAudit(serverId);
      setReloadToken((t) => t + 1);
    } catch (err) {
      setAuditError(err instanceof Error ? err : new Error(String(err)));
      setIsRunningAudit(false);
    }
  };

  const handleGenerateToken = async () => {
    if (!serverId) return;
    setIsGeneratingToken(true);
    try {
      const res = await api.generateServerInstallToken(serverId);
      setReinstallToken(res.installation_command);
    } catch (err) {
      setAuditError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsGeneratingToken(false);
    }
  };

  const handleDelete = async () => {
    if (!serverId) return;
    setIsDeleting(true);
    try {
      await api.deleteServer(serverId);
      navigate('/app/servers');
    } catch (err) {
      // Close the dialog so the refusal (and the environments that caused
      // it) is readable; nothing was changed on the server.
      setShowDeleteConfirm(false);
      setAuditError(err instanceof Error ? err : new Error(String(err)));
      setIsDeleting(false);
    }
  };

  const toggleExpand = (checkId: string) => {
    setExpandedChecks((prev) => ({ ...prev, [checkId]: !prev[checkId] }));
  };

  // Problems first (audit SR1): failures, then warnings; passed and
  // informational checks sit behind a disclosure.
  const visibleChecks = useMemo(
    () => (latestAudit?.checks ?? []).filter((check) => activeFilter === 'all' || check.status === activeFilter),
    [latestAudit, activeFilter],
  );
  const problemChecks = useMemo(
    () => visibleChecks.filter((check) => check.status === 'fail' || check.status === 'warning').sort((x, y) => (x.status === y.status ? 0 : x.status === 'fail' ? -1 : 1)),
    [visibleChecks],
  );
  const quietChecks = useMemo(() => visibleChecks.filter((check) => check.status === 'pass' || check.status === 'info'), [visibleChecks]);

  const capabilities = server?.capabilities || [];
  const groupedCapabilities = useMemo(() => {
    const groups: Record<string, ServerCapability[]> = {};
    for (const cap of capabilities) {
      const cat = cap.category || 'other';
      if (!groups[cat]) {
        groups[cat] = [];
      }
      groups[cat].push(cap);
    }
    return groups;
  }, [capabilities]);
  const capabilitySummary = useMemo(() => {
    const order = ['blocked', 'supported_with_warning', 'recommended', 'supported', 'available', 'unknown'];
    const counts = new Map<string, number>();
    for (const cap of capabilities) counts.set(cap.status, (counts.get(cap.status) ?? 0) + 1);
    return [...counts.entries()].sort((x, y) => order.indexOf(x[0]) - order.indexOf(y[0]));
  }, [capabilities]);
  // Blocking verdicts first: they are what stops work on this server.
  const capabilityExceptions = useMemo(
    () =>
      capabilities
        .filter((cap) => cap.status === 'blocked' || cap.status === 'supported_with_warning')
        .sort((x, y) => Number(y.impact === 'blocking') - Number(x.impact === 'blocking')),
    [capabilities],
  );

  // Only the first load shows the skeleton. A reload (after a check, or a
  // live event) keeps the page, its filters and open disclosures in place.
  if (serverResource.loading && !serverResource.data) {
    return <SkeletonPage />;
  }

  if (serverResource.error || !server) {
    return (
      <div className="page">
        <ErrorNotice error={serverResource.error || new Error('Server not found')} />
        <Link to="/app/servers" className="button button--secondary">
          Back to servers
        </Link>
      </div>
    );
  }

  return (
    <Page>
      <PageHeader
        breadcrumbs={[{ label: 'Servers', to: '/app/servers' }, { label: server.name }]}
        title={server.name}
        meta={
          <>
            {hosting ? (
              <LevelBadge level={hosting.agent.level}>{agentStateLabel[hosting.agent.state] ?? hosting.agent.state}</LevelBadge>
            ) : (
              <StatusBadge kind="serverAgent" value={server.status} />
            )}
            <span className="font-mono">{server.hostname || 'Hostname not reported yet'}</span>
            <span>Agent {server.agent_version ? `v${server.agent_version}` : 'not installed yet'}</span>
          </>
        }
        actions={
          <>
            <button type="button" className="button button--secondary" onClick={handleGenerateToken} disabled={isGeneratingToken}>
              {isGeneratingToken ? 'Generating' : server.status === 'waiting_for_agent' ? 'Generate install command' : 'Generate reinstall command'}
            </button>
            <button
              type="button"
              className="button button--primary"
              onClick={handleRunAudit}
              disabled={isRunningAudit || server.status !== 'online'}
              title={server.status !== 'online' ? 'The agent must be connected to check the server.' : undefined}
            >
              {isRunningAudit ? (
                <>
                  <Icon name="spinner" />
                  Checking server
                </>
              ) : (
                <>
                  <Icon name="refresh" />
                  {latestAudit ? 'Check server again' : 'Check server'}
                </>
              )}
            </button>
            <ActionMenu
              label="More server actions"
              items={[{ label: 'Remove server', danger: true, onClick: () => setShowDeleteConfirm(true) }]}
            />
          </>
        }
      />

      {auditError && (serverInUseDetails(auditError) ? <ServerInUseNotice error={auditError} /> : <ErrorNotice error={auditError} />)}

      {auditStalledAt !== null && !isRunningAudit ? (
        <Callout
          tone="warning"
          title="The check has not finished"
          testId="server-check-stalled"
          className="server-check-stalled"
          actions={
            <button type="button" className="button button--secondary button--small" onClick={handleRunAudit} disabled={server.status !== 'online'}>
              {actionLabel.retry}
            </button>
          }
        >
          <p>
            Started {relativeTime(new Date(auditStalledAt - SERVER_CHECK_WAIT_MS).toISOString())}, no result received. Cause unknown.
            {server.status === 'online' ? '' : ' The agent is not connected, so a check cannot run until it is.'}
          </p>
        </Callout>
      ) : null}

      {reinstallToken && (
        <Section
          title="Run this on the server as root"
          description="Single-use token · expires in 15 minutes"
          actions={
            <button type="button" className="button button--secondary button--small" onClick={() => navigator.clipboard.writeText(reinstallToken)}>
              <Icon name="copy" />
              Copy
            </button>
          }
        >
          <pre className="command-block">
            <code>{reinstallToken}</code>
          </pre>
        </Section>
      )}

      <SummaryTiles label="Server summary">
        <SummaryTile
          label="System"
          headline={server.os || 'Operating system not reported yet'}
          detail={[server.architecture, kernelRelease(server.kernel)].filter(Boolean).join(' · ') || undefined}
        />
        {/* The agent's state is the header's badge; the tile adds what the badge does not: when it was last heard from. */}
        <SummaryTile
          label="Last contact"
          level={hosting ? hosting.agent.level : server.status === 'online' ? 'ok' : server.status === 'waiting_for_agent' ? 'warning' : 'critical'}
          headline={server.last_seen_at ? relativeTime(server.last_seen_at) : 'Never'}
          detail={hosting ? hosting.agent.fact : undefined}
          meta={`Added ${new Date(server.created_at).toLocaleDateString()}`}
          testId="server-agent"
        />
        <SummaryTile
          label="Server check"
          level={latestAudit ? toneLevel[statusFor('serverCheck', auditOverall[latestAudit.status]).tone] : 'unknown'}
          headline={statusFor('serverCheck', latestAudit ? auditOverall[latestAudit.status] : 'not_run').label}
          detail={
            latestAudit
              ? latestAudit.summary.failed + latestAudit.summary.warnings > 0
                ? `${latestAudit.summary.failed + latestAudit.summary.warnings}/${latestAudit.summary.passed + latestAudit.summary.warnings + latestAudit.summary.failed} checks need a look`
                : undefined
              : 'Runs when the agent connects'
          }
          meta={latestAudit ? (checkIsOld ? `Checked ${relativeTime(latestAudit.created_at)} · out of date` : `Checked ${relativeTime(latestAudit.created_at)}`) : undefined}
          testId="server-check-tile"
        />
      </SummaryTiles>

      <Section
        title="What runs here"
        testId="server-hosts"
      >
        {hosting ? (
          <HostedEnvironments environments={hosting.environments} />
        ) : health.loading && !health.data ? (
          <QuietEmpty>Reading…</QuietEmpty>
        ) : (
          <QuietEmpty>What runs here could not be read.</QuietEmpty>
        )}
      </Section>

      {serverId ? <ServerEdgeSection serverId={serverId} /> : null}

      {isOwner ? <ServerCloudSection server={server} /> : null}

      {canOpenTerminal ? <ServerTerminalSection server={server} canOpen={canOpenTerminal} isOwner={isOwner} /> : null}

      <Section
        title={
          <>
            What Commerce Ops can do on this server
            <HelpTip label="Software already on this server">Software that already runs here is never changed or stopped by Commerce Ops.</HelpTip>
          </>
        }
        testId="server-capabilities"
      >
        {capabilities.length === 0 ? (
          <QuietEmpty>Available after the first server check</QuietEmpty>
        ) : (
          <>
            <p className="summary-line" data-testid="capability-summary">
              {capabilitySummary.map(([status, count]) => (
                <StatusBadge
                  key={status}
                  kind="capability"
                  value={checkIsOld && statusFor('capability', status).tone === 'success' ? 'unknown' : status}
                  label={`${count} ${statusFor('capability', status).label.toLowerCase()}`}
                  title={checkIsOld && statusFor('capability', status).tone === 'success' ? 'Out of date' : undefined}
                />
              ))}
              {latestAudit ? (
                <span className="type-meta" data-testid="capability-freshness">
                  Checked <time dateTime={latestAudit.created_at} title={new Date(latestAudit.created_at).toLocaleString()}>{relativeTime(latestAudit.created_at)}</time>
                  {checkIsOld ? ' · out of date' : ''}
                </span>
              ) : null}
            </p>
            {capabilityExceptions.length > 0 ? (
              <ul className="capability-list" aria-label="Capabilities that need a look">
                {capabilityExceptions.map((cap) => (
                  <CapabilityRow key={cap.id} cap={cap} checkedAt={latestAudit?.created_at} />
                ))}
              </ul>
            ) : null}
            <details className="disclosure" open={showAllCapabilities} onToggle={(event) => setShowAllCapabilities((event.target as HTMLDetailsElement).open)}>
              <summary>{showAllCapabilities ? 'Hide' : 'Show'} all {capabilities.length} capabilities</summary>
              {showAllCapabilities ? (
                <div className="capability-groups">
                  {Object.entries(groupedCapabilities).map(([catKey, caps]) => (
                    <Subsection key={catKey} title={CAPABILITY_CATEGORY_TITLES[catKey] || humanize(catKey)}>
                      <ul className="capability-list">
                        {caps.map((cap) => (
                          <CapabilityRow key={cap.id} cap={cap} checkedAt={latestAudit?.created_at} />
                        ))}
                      </ul>
                    </Subsection>
                  ))}
                </div>
              ) : null}
            </details>
          </>
        )}
      </Section>

      <Section
        title={
          <>
            Server check
            <HelpTip label="What a server check does">A read-only inspection by the agent. It changes nothing on the server.</HelpTip>
          </>
        }
        testId="server-check"
      >
        {/* Filters narrow the one list of checks; problems first. Failed
            stays visible at 0 so an active filter never loses its chip. */}
        {latestAudit ? (
          <div className="server-check__filters">
            <FilterChips
              label="Show checks by result"
              value={activeFilter}
              onChange={setActiveFilter}
              options={[
                { value: 'all', label: 'All', count: latestAudit.checks.length },
                { value: 'fail', label: 'Failed', count: latestAudit.summary.failed },
                { value: 'warning', label: 'Warnings', count: latestAudit.summary.warnings },
                { value: 'pass', label: 'Passed', count: latestAudit.summary.passed },
              ]}
            />
          </div>
        ) : null}

        {!latestAudit ? (
          <div className="stack">
            <QuietEmpty>
              {server.status === 'online' ? 'Not checked' : 'Runs when the agent connects'}
            </QuietEmpty>
            {server.status === 'online' && (
              <button type="button" className="button button--primary" onClick={handleRunAudit} disabled={isRunningAudit}>
                Check server now
              </button>
            )}
          </div>
        ) : (
          <>
            {problemChecks.length > 0 ? (
              <Subsection title={`Needs a look (${problemChecks.length})`}>
                <CheckList checks={problemChecks} expanded={expandedChecks} onToggle={toggleExpand} categoryTitles={CATEGORY_TITLES} />
              </Subsection>
            ) : null}
            {quietChecks.length > 0 ? (
              <details className="disclosure" open={activeFilter === 'pass'}>
                <summary>
                  {activeFilter === 'pass' ? 'Passed' : 'Passed and informational'} ({quietChecks.length})
                </summary>
                <CheckList checks={quietChecks} expanded={expandedChecks} onToggle={toggleExpand} categoryTitles={CATEGORY_TITLES} />
              </details>
            ) : null}
          </>
        )}
      </Section>

      {showDeleteConfirm && (
        <ConfirmDialog
          open={true}
          title={`Remove ${server.name} from Commerce Ops?`}
          description={serverRemovalConsequences}
          confirmLabel="Remove server"
          confirmText={server.name}
          danger={true}
          busy={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </Page>
  );
}
